import React, { useState } from 'react'
import PropTypes from 'prop-types'
import mime from 'mime-types'

const InputFileReader = ({
  onFileLoad,
  children,
  accept = ['.csv', '.xls', '.xlsx', '.txt'],
  capture = false,
  multiple = false,
}) => {
  const [hasError, setHasError] = useState(false)
  const [isDragging, setIsDragging] = useState(false)

  const fileInputId = 'input-file-reader-file'

  const fileInputStyle = {
    width: '0.1px',
    height: '0.1px',
    opacity: 0,
    overflow: 'hidden',
    position: 'absolute',
    zIndex: -1
  }

  const suppress = (event) => {
    event.stopPropagation()
    event.preventDefault()
  }

  const validateMimeType = (file) => {
    const acceptedMimeTypes = accept.map((ext) => mime.lookup(ext))
    return acceptedMimeTypes.includes(file.type)
  }

  const handleFileLoad = (file) => {
    setHasError(false)

    if (!validateMimeType(file)) {
      setHasError(true)
      return
    }

    if (window.FileReader) {
      const reader = new FileReader()
      const hasReadAsBinaryString = !!reader.readAsBinaryString

      reader.onload = (event) => onFileLoad(event.target.result)

      const method = hasReadAsBinaryString ? 'readAsBinaryString' : 'readAsArrayBuffer'
      reader[method](file)
    } else {
      console.error("Sorry, your browser doesn't support file uploads")
    }
  }

  const handleDragOverEnter = (event) => {
    suppress(event)
    setIsDragging(true)
  }

  const handleDragLeaveEnd = (event) => {
    suppress(event)
    setHasError(false)
    setIsDragging(false)
  }

  const handleDrop = (event) => {
    handleDragLeaveEnd(event)
    const files = event.dataTransfer.files

    if (files?.[0]) {
      handleFileLoad(files[0])
    }
  }

  const handleChange = (event) => {
    const files = event.target.files

    if (files?.[0]) {
      handleFileLoad(files[0])
    }

    event.target.value = ''
  }

  const childrenWithProps = React.Children.map(children, (child) => {
    if (!React.isValidElement(child)) return child

    return React.cloneElement(child, {
      accept,
      hasError,
      isDragging,
      fileInputId
    })
  })

  return (
    <div
      className="input-file-reader-box"
      onDrag={suppress}
      onDragStart={suppress}
      onDragOver={handleDragOverEnter}
      onDragEnter={handleDragOverEnter}
      onDragLeave={handleDragLeaveEnd}
      onDragEnd={handleDragLeaveEnd}
      onDrop={handleDrop}
    >
      <input
        type="file"
        id={fileInputId}
        accept={Array.isArray(accept) ? accept.join(',') : accept}
        multiple={multiple}
        capture={capture}
        onChange={handleChange}
        style={fileInputStyle}
      />

      {childrenWithProps}
    </div>
  )
}

InputFileReader.propTypes = {
  accept: PropTypes.arrayOf(PropTypes.string),
  capture: PropTypes.bool,
  multiple: PropTypes.bool,
  onFileLoad: PropTypes.func.isRequired,
  children: PropTypes.node,
}

export default InputFileReader
